/**
 * addItemGroupsState.js — Pure reducer + selectors for the PR-6 per-sub-area
 * Add Item wizard.
 *
 * Ownership: driven by AddItemGroups.js via useReducer. No React, no DOM.
 *
 * Group state shape:
 * {
 *   subAreaId: string,
 *   subAreaName: string,
 *   mode: 'collapsed' | 'active',
 *   activeDraft: {
 *     categoryId, productId, sqft,
 *     note, addlLabor, addlCost,
 *     accessories: [],
 *     _editingLineId?: string,   // set only when Edit reopened an existing row
 *   } | null,
 *   committedLines: [
 *     { id, categoryId, productId, product, sqft, unit,
 *       note, addlLabor, addlCost,
 *       accessories: [], alternates: [] }   // field name matches rest of codebase
 *   ]
 * }
 *
 * Container state:
 * { groups: Group[], nudge?: { subAreaId, message } }
 *
 * Rules:
 * - INIT_GROUPS: first group `active` with empty draft, rest `collapsed` (Q2-d).
 * - SAVE_ITEM: commits activeDraft into committedLines. If _editingLineId is
 *   set, overwrites the existing row in place. Group returns to `collapsed`.
 *   Does NOT auto-open the next group.
 * - ACTIVATE_GROUP: activates the target group with an empty draft.
 *   Multiple groups may be active simultaneously — drafts are per-sub-area
 *   and independent (PR-58/B6, 347_ §4). Does NOT deactivate other groups.
 * - START_EDIT: activates the target with activeDraft prefilled from the row.
 *   Same independence guarantee as ACTIVATE_GROUP — does NOT deactivate any
 *   other active group.
 * - INIT_FOR_EDIT (IDI-176): entry point for the wizard-as-editor path.
 *   Takes an existing quote line item (`initialLine`) plus its target
 *   `subAreaId`, seeds it into that group's `committedLines` (if not
 *   already present), then activates the group with a prefilled draft —
 *   the same draft-shape START_EDIT produces. This is what lets the wizard
 *   reopen against an ALREADY-COMMITTED (persisted) line rather than only
 *   lines committed inside the current wizard session.
 * - DELETE_LINE / ADD_ALTERNATIVE: no activation impact.
 * - RESET: fresh state (used on close/reopen).
 *
 * Selectors:
 * - hasDirtyDraft(state): true iff any active group has any draft field set.
 * - commitReady(state): array of { subAreaId, subAreaName, lines }, filtering
 *   out groups with committedLines.length === 0 (R16 silent-drop; see
 *   Portica-proposal note below).
 */

import { ensureAccessoryRowIds } from './accessoryRowIds';

// ─────────────────────────────────────────────────────────────────────────
// Draft/line construction helpers
// ─────────────────────────────────────────────────────────────────────────

export function emptyDraft() {
  return {
    categoryId: null,
    productId: null,
    sqft: '',
    note: '',
    addlLabor: '',
    addlCost: '',
    // PR-62 §3aj: per-line display-name override for the customer document.
    // Optional; falls through to item_name in QuoteSelectionPanel when blank.
    alias: '',
    // IDI-173: the Coverage dropdown choice ("R-30" / "3in"); null for
    // products without a chart.
    coverageChoice: null,
    accessories: [],
    _editingLineId: null,
  };
}

/**
 * IDI-176 B1 (2026-09-05) · shared reader for the wizard-as-editor entry.
 *
 * Two vocabularies live in this system and it's the writer's job to bridge:
 *
 *   DRAFT (what this reducer's activeDraft holds; the wizard's input form):
 *     { note, addlLabor, addlCost, ... }
 *
 *   COMMITTED (what `buildLineItem(draft)` emits and what QuoteBuilderPage
 *     stores in state as a line item; the writer at seedCatalog.js:869-871):
 *     { notes, additionalLabor, additionalCost, ... }
 *
 * The bridge is one-way today (draft → committed, at buildLineItem's write
 * side). When we RE-enter the wizard against a committed line — i.e., the
 * user hit Edit — we need to convert BACK: read the committed keys and
 * populate a draft shape. This is that conversion.
 *
 * Failure mode attached: use of the WRONG vocabulary here — reading `.note`
 * off a committed line — resolves to undefined, prefills the draft with ''
 * / null / [], and when SAVE_ITEM later spreads that empty draft over the
 * existing row via handleEditItem's `{ ...li, ...updates }` merge in
 * QuoteBuilderPage.js:1223, the note/labor/cost fields are DESTROYED on
 * save. This function is the single reader used by BOTH START_EDIT and
 * INIT_FOR_EDIT so the vocabulary can never drift between them again.
 *
 * If a future writer changes the committed shape (e.g., renames `notes` to
 * `note` per some sensible consolidation), grep for `draftFromCommittedLine`
 * to find the single point of change here — do NOT patch the reducer cases
 * individually. That drift is exactly what shipped in #363.
 */
export function draftFromCommittedLine(row) {
  if (!row) return null;
  return {
    coverageChoice: row?.coverageChoice ?? null,
    categoryId: row.categoryId,
    productId: row.productId,
    sqft: row.sqft != null ? String(row.sqft) : '',
    // Read committed vocabulary. `.note` / `.addlLabor` / `.addlCost` are
    // draft names and DO NOT appear on committed lines — reading them here
    // is what caused #363's data-loss regression.
    note: row.notes || '',
    addlLabor: row.additionalLabor != null && row.additionalLabor !== ''
      ? String(row.additionalLabor) : '',
    addlCost: row.additionalCost != null && row.additionalCost !== ''
      ? String(row.additionalCost) : '',
    // IDI-442: saved rows that predate per-row ids get `<line id>:acc:<i>`.
    accessories: Array.isArray(row.accessories)
      ? ensureAccessoryRowIds(row.accessories.map((a) => ({ ...a })), row.id)
      : [],
    // PR-62 §3aj: prefill alias when editing an existing line.
    alias: row.alias || '',
    _editingLineId: row.id,
  };
}

/** Truthy iff draft has any user-entered content worth saving/blocking. */
export function isDraftDirty(draft) {
  if (!draft) return false;
  return Boolean(
    draft.categoryId ||
    draft.productId ||
    (draft.sqft !== '' && draft.sqft != null) ||
    (draft.note && draft.note.length) ||
    (draft.addlLabor !== '' && draft.addlLabor != null && draft.addlLabor !== '') ||
    (draft.addlCost !== '' && draft.addlCost != null && draft.addlCost !== '') ||
    (draft.alias && draft.alias.length) ||
    (Array.isArray(draft.accessories) && draft.accessories.length > 0) ||
    draft._editingLineId
  );
}

/** Truthy iff draft has the minimum fields to be committed. */
export function isDraftComplete(draft) {
  if (!draft) return false;
  return Boolean(
    draft.categoryId &&
    draft.productId &&
    Number(draft.sqft) > 0 &&
    // IDI-173: a product whose Coverage dropdown has more than one entry
    // needs a choice before it can be priced (GroupCard stamps
    // _coverageOptionCount from the resolved product). Without this the
    // line would price at $0 or buildLineItem would refuse it.
    !((draft._coverageOptionCount || 0) > 1 && !draft.coverageChoice)
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Initial state
// ─────────────────────────────────────────────────────────────────────────

export function initialState(subAreas = []) {
  return reducer({ groups: [], nudge: null }, { type: 'INIT_GROUPS', subAreas });
}

// ─────────────────────────────────────────────────────────────────────────
// Reducer
// ─────────────────────────────────────────────────────────────────────────

export function reducer(state, action) {
  switch (action.type) {
    case 'INIT_GROUPS': {
      const subAreas = Array.isArray(action.subAreas) ? action.subAreas : [];
      const groups = subAreas.map((sa, idx) => ({
        subAreaId: sa.id,
        subAreaName: sa.name,
        mode: idx === 0 ? 'active' : 'collapsed',
        activeDraft: idx === 0 ? emptyDraft() : null,
        committedLines: [],
      }));
      return { groups, nudge: null };
    }

    case 'RESET':
      return { groups: [], nudge: null };

    case 'CLEAR_NUDGE':
      return { ...state, nudge: null };

    case 'ACTIVATE_GROUP': {
      const targetId = action.subAreaId;
      const target = state.groups.find((g) => g.subAreaId === targetId);
      if (!target) return state;
      if (target.mode === 'active') return state; // already active, no-op

      // PR-58 / B6 (§347 §4): the cross-group "complete item in X first"
      // blocker is REMOVED. Portica's ruling, verbatim: "the wizard's model
      // is 'one draft at a time is a lie the code tells itself.'"
      //
      // Drafts are per-sub-area and truly independent — each group carries
      // its own `activeDraft`, so multiple `mode === 'active'` groups can
      // coexist without any collision. SAVE_ITEM is already per-subAreaId.
      //
      // Activation does NOT deactivate other active groups anymore. Users
      // can open drafts in multiple sub-areas simultaneously.
      const groups = state.groups.map((g) => {
        if (g.subAreaId === targetId) {
          return { ...g, mode: 'active', activeDraft: emptyDraft() };
        }
        return g;
      });
      return { groups, nudge: null };
    }

    case 'UPDATE_DRAFT': {
      const { subAreaId, patch } = action;
      const groups = state.groups.map((g) => {
        if (g.subAreaId !== subAreaId) return g;
        if (g.mode !== 'active' || !g.activeDraft) return g;
        return { ...g, activeDraft: { ...g.activeDraft, ...patch } };
      });
      return { ...state, groups, nudge: null };
    }

    case 'SAVE_ITEM': {
      const { subAreaId, buildLine } = action;
      // `buildLine(draft)` is provided by the caller so this module stays
      // catalog-agnostic. It should return the committed-line shape
      // (id, product, unit, price, etc.).
      //
      // IDI-417 (2026-09-24): call shape is `buildLine({ subAreaId, draft })`,
      // matching commitReady() below. This branch previously called
      // `buildLine(g.activeDraft)` (the raw draft), so the real writer
      // (AddItemCascade.buildLineFromDraft, which destructures
      // `{ subAreaId, draft }`) received `draft === undefined` and stamped
      // every field undefined -- surfacing as "Add Alternate / For: undefined"
      // plus a lost sub-area and square footage on the committed line.
      // The test double `testBuildLine = (draft) => ...` mirrored the WRONG
      // shape, so the suite stayed green while production broke.
      const groups = state.groups.map((g) => {
        if (g.subAreaId !== subAreaId) return g;
        if (g.mode !== 'active' || !g.activeDraft) return g;
        if (!isDraftComplete(g.activeDraft)) return g;

        const newLine = buildLine({ subAreaId: g.subAreaId, draft: g.activeDraft });
        let committedLines;
        if (g.activeDraft._editingLineId) {
          // Overwrite existing row identified by _editingLineId. Preserve any
          // alternates that were added to the committed row after original save.
          committedLines = g.committedLines.map((line) =>
            line.id === g.activeDraft._editingLineId
              ? { ...newLine, id: g.activeDraft._editingLineId, alternates: line.alternates || [] }
              : line
          );
        } else {
          committedLines = [...g.committedLines, newLine];
        }
        return { ...g, mode: 'collapsed', activeDraft: null, committedLines };
      });
      return { ...state, groups, nudge: null };
    }

    case 'START_EDIT': {
      const { subAreaId, lineId } = action;
      const target = state.groups.find((g) => g.subAreaId === subAreaId);
      if (!target) return state;
      const row = target.committedLines.find((l) => l.id === lineId);
      if (!row) return state;

      // PR-58 / B6: cross-group blocker REMOVED (see ACTIVATE_GROUP for
      // the full rationale). Editing a committed line in one sub-area does
      // not affect drafts in another sub-area — each group's draft is its
      // own state.
      //
      // IDI-176 B1 (2026-09-05): draft-shape reader is now the shared
      // draftFromCommittedLine() helper above — reads committed vocabulary
      // (notes / additionalLabor / additionalCost). The previous inline
      // block read DRAFT vocabulary (`row.note`, `row.addlLabor`,
      // `row.addlCost`) off a COMMITTED row → empty draft → data loss on
      // save. #363 shipped that defect; #<next PR> fixed it here.
      const prefilledDraft = draftFromCommittedLine(row);

      const groups = state.groups.map((g) => {
        if (g.subAreaId === subAreaId) {
          return { ...g, mode: 'active', activeDraft: prefilledDraft };
        }
        return g;
      });
      return { groups, nudge: null };
    }

    case 'INIT_FOR_EDIT': {
      // IDI-176 · wizard-as-editor entry point.
      //
      // The caller has an existing quote-level line item and wants to edit
      // it inside AddItemCascade. Seed `committedLines` with the row (so
      // START_EDIT semantics find it), then dispatch the same prefill the
      // in-session edit path uses.
      //
      // Failure modes attached:
      //  · If subAreaId matches no group, this is a no-op (returns state
      //    unchanged). The wizard's edit-mode entry must ensure the group
      //    exists — otherwise the user sees a silent failure.
      //  · If a row with the same id is already in committedLines, we do
      //    NOT re-append; the existing row wins. This makes INIT_FOR_EDIT
      //    idempotent across accidental double-dispatches.
      const { subAreaId, initialLine } = action;
      if (!initialLine || !initialLine.id) return state;
      const target = state.groups.find((g) => g.subAreaId === subAreaId);
      if (!target) return state;

      const alreadyPresent = target.committedLines.some((l) => l.id === initialLine.id);
      const seededLines = alreadyPresent
        ? target.committedLines
        : [...target.committedLines, initialLine];

      const prefilledDraft = draftFromCommittedLine(initialLine);

      const groups = state.groups.map((g) => {
        if (g.subAreaId !== subAreaId) return g;
        return {
          ...g,
          mode: 'active',
          committedLines: seededLines,
          activeDraft: prefilledDraft,
        };
      });
      return { ...state, groups, nudge: null };
    }

    case 'CANCEL_EDIT': {
      const { subAreaId } = action;
      const groups = state.groups.map((g) => {
        if (g.subAreaId !== subAreaId) return g;
        return { ...g, mode: 'collapsed', activeDraft: null };
      });
      return { ...state, groups, nudge: null };
    }

    case 'DELETE_LINE': {
      const { subAreaId, lineId } = action;
      const groups = state.groups.map((g) => {
        if (g.subAreaId !== subAreaId) return g;
        return { ...g, committedLines: g.committedLines.filter((l) => l.id !== lineId) };
      });
      return { ...state, groups, nudge: null };
    }

    case 'ADD_ALTERNATIVE': {
      const { subAreaId, lineId, alternative } = action;
      const groups = state.groups.map((g) => {
        if (g.subAreaId !== subAreaId) return g;
        return {
          ...g,
          committedLines: g.committedLines.map((l) =>
            l.id === lineId
              ? { ...l, alternates: [...(l.alternates || []), alternative] }
              : l
          ),
        };
      });
      return { ...state, groups };
    }

    default:
      return state;
  }
}

// ─────────────────────────────────────────────────────────────────────────
// Selectors
// ─────────────────────────────────────────────────────────────────────────

/** True iff any active group has a dirty draft. */
export function hasDirtyDraft(state) {
  if (!state || !Array.isArray(state.groups)) return false;
  return state.groups.some((g) => g.mode === 'active' && isDraftDirty(g.activeDraft));
}

/**
 * Return committed groups ready for onCommit. Groups with zero committed
 * lines are silently dropped (R16).
 *
 * // PR-6: Portica-proposed silent-drop of empty sub-areas at Add-to-quote.
 * // Not a stakeholder-decided rule — flip to a hard error later if desired
 * // (one-liner change: replace `.filter` with a check that raises).
 */
export function commitReady(state, buildLine) {
  if (!state || !Array.isArray(state.groups)) return [];
  const kept = state.groups
    .map((g) => {
      // GP-17: include any complete active draft as a committed line.
      let lines = [...g.committedLines];
      if (buildLine && g.mode === 'active' && isDraftComplete(g.activeDraft)) {
        const built = buildLine({ subAreaId: g.subAreaId, draft: g.activeDraft });
        const editingId = g.activeDraft._editingLineId;
        if (editingId && lines.some((l) => l.id === editingId)) {
          // IDI-365: the draft is an EDIT of a committed row (INIT_FOR_EDIT
          // seeds the original into committedLines AND a draft of it;
          // START_EDIT drafts an in-session row). REPLACE that row in place,
          // keeping its id and alternates, exactly like SAVE_ITEM. Before
          // this the original was emitted FIRST and the edit appended after
          // it; AddItemCascade hands the edit target to the FIRST line, so
          // onEditItem received the UNEDITED values and the edited draft went
          // to a no-op onAddItem. That was the "edit is lost" bug (walks
          // 09-24, 09-25, 09-29).
          lines = lines.map((l) => (l.id === editingId
            ? { ...built, id: editingId, alternates: l.alternates || [] }
            : l));
        } else {
          lines.push(built);
        }
      }
      return { subAreaId: g.subAreaId, subAreaName: g.subAreaName, lines };
    })
    .filter((g) => g.lines.length > 0);

  // Diagnostic: log dropped sub-areas so devs/stakeholders can see the
  // silent-drop in action during QA. Not user-facing.
  const keptIds = new Set(kept.map(g => g.subAreaId));
  const dropped = state.groups
    .filter((g) => !keptIds.has(g.subAreaId))
    .map((g) => g.subAreaName);
  if (dropped.length && typeof console !== 'undefined' && console.debug) {
    // eslint-disable-next-line no-console
    console.debug(
      // PR-6: Portica-proposed silent-drop, not stakeholder-decided
      '[PR-6] Silently dropping empty sub-areas at Add-to-quote:',
      dropped
    );
  }
  return kept;
}
